import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from 'react';
import {
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type ColumnSizingState,
  type Row,
  type Table,
} from '@tanstack/react-table';
import { getWpPinnedStyles, type WpColumnPinning } from '../dataTablePinning.js';
import { colgroupPercentWidth, computeColumnSizingFromSpecs } from '../dataTableSizing.js';
import {
  DATA_TABLE_DEFAULT_COLUMN_WIDTH_PX,
  DATA_TABLE_HORIZONTAL_SCROLL_REF_PX,
  DATA_TABLE_MIN_COLUMN_WIDTH_PX,
  DATA_TABLE_PIN_START_NARROW_MAX_PX,
  parseDataTableColumnWidth,
  type DataTableColumnWidthMap,
} from './columnWidth.js';
import type { Column } from './types.js';

export { buildWpColumnPinning, getWpPinnedStyles, wpPinSide } from '../dataTablePinning.js';
export type { WpColumnPinning } from '../dataTablePinning.js';
export type { DataTableColumnWidthMap };

export const ACTION_COLUMN_WIDTH_PX = 132;
export const ACTION_COLUMN_COMPACT_WIDTH_PX = 84;
/** Compact leading-only toolbar (no ⋮ menu column) — e.g. variant readiness pill. */
export const ACTION_COLUMN_COMPACT_LEADING_ONLY_WIDTH_PX = 144;
/** Leading pill (readiness + primary + menu) needs more room than menu-only. */
export const ACTION_COLUMN_WITH_LEADING_WIDTH_PX = 184;

function sumColumnWidths(
  sizing: ColumnSizingState,
  columnKeys: string[],
  hasActionColumn: boolean,
  actionColumnWidthPx: number,
): number {
  let sum = columnKeys.reduce((acc, key) => acc + (sizing[key] ?? DATA_TABLE_MIN_COLUMN_WIDTH_PX), 0);
  if (hasActionColumn) sum += actionColumnWidthPx;
  return sum;
}

function columnWidthStyle(_px: number | undefined, declarativeWidth?: string): CSSProperties {
  if (!declarativeWidth) return {};
  return { width: declarativeWidth };
}

function horizontalScrollPinStartStyle(): CSSProperties {
  return {
    width: 'var(--wp-datatable-sticky-start-width, 11rem)',
    minWidth: 'var(--wp-datatable-sticky-start-min, 8.5rem)',
    maxWidth: 'var(--wp-datatable-sticky-start-width, 11rem)',
    boxSizing: 'border-box',
  };
}

function horizontalScrollDataColumnStyle(declarativeWidth?: string): CSSProperties {
  if (!declarativeWidth?.trim()) {
    return { minWidth: 'var(--wp-datatable-scroll-col-min, 9.5rem)' };
  }
  const raw = declarativeWidth.trim();
  const px = parseDataTableColumnWidth(
    raw,
    DATA_TABLE_HORIZONTAL_SCROLL_REF_PX,
    DATA_TABLE_DEFAULT_COLUMN_WIDTH_PX,
  );
  const w = `${px}px`;
  return { width: w, minWidth: w };
}

function clampPinStartSizingPx(
  sizing: ColumnSizingState,
  pinStartKey: string | undefined,
): ColumnSizingState {
  if (!pinStartKey || sizing[pinStartKey] == null) return sizing;
  const capped = Math.min(sizing[pinStartKey]!, DATA_TABLE_PIN_START_NARROW_MAX_PX);
  if (capped === sizing[pinStartKey]) return sizing;
  return { ...sizing, [pinStartKey]: capped };
}

export type DataTableCoreMeta<T> = {
  wpColumn?: Column<T>;
};

function buildColumnDefs<T>(
  columns: Column<T>[],
  hasActionColumn: boolean,
  actionColumnWidthPx: number,
): ColumnDef<T, unknown>[] {
  const defs: ColumnDef<T, unknown>[] = columns.map((col) => ({
    id: col.key,
    accessorFn: (row) => (row as Record<string, unknown>)[col.key],
    header: col.label as string,
    cell: (ctx) => {
      const wp = col;
      return wp.render
        ? wp.render(ctx.row.original)
        : String((ctx.row.original as Record<string, unknown>)[col.key] ?? '');
    },
    size: parseDataTableColumnWidth(col.width, 800, DATA_TABLE_DEFAULT_COLUMN_WIDTH_PX),
    minSize: DATA_TABLE_MIN_COLUMN_WIDTH_PX,
    enableResizing: true,
    meta: { wpColumn: col } satisfies DataTableCoreMeta<T>,
  }));

  if (hasActionColumn) {
    defs.push({
      id: '__actions',
      header: '',
      cell: () => null,
      size: actionColumnWidthPx,
      minSize: actionColumnWidthPx,
      enableResizing: false,
      meta: { wpColumn: undefined },
    });
  }

  return defs;
}

export function useDataTableCore<T>({
  data,
  columns,
  rowKey,
  hasActionColumn,
  columnResizeEnabled,
  columnPinning,
  enableColumnPinning = false,
  stickyEndInsetPx = 0,
  compactActions,
  hasRowLeadingActions = false,
  hasRowActionsMenu = false,
  tableRef,
  horizontalScrollLayout = false,
  controlledSizing,
  onSizingChange,
}: {
  data: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string;
  hasActionColumn: boolean;
  columnResizeEnabled: boolean;
  columnPinning: WpColumnPinning;
  enableColumnPinning?: boolean;
  stickyEndInsetPx?: number;
  compactActions: boolean;
  hasRowLeadingActions?: boolean;
  hasRowActionsMenu?: boolean;
  tableRef: RefObject<HTMLTableElement | null>;
  /** Sticky horizontal scroll — keep declarative column widths (px/rem/%). */
  horizontalScrollLayout?: boolean;
  controlledSizing?: DataTableColumnWidthMap;
  onSizingChange?: (sizing: DataTableColumnWidthMap) => void;
}): {
  table: Table<T>;
  colGroupCols: { key: string; widthPx: number; widthPct: string }[] | null;
  tableWidthPx: number | undefined;
  getColumnStyle: (key: string, declarativeWidth?: string) => CSSProperties;
  mergeCellStyles: (key: string, declarativeWidth?: string, isHeader?: boolean) => CSSProperties;
  onResizePointerDown: (
    key: string,
    declarativeWidth: string | undefined,
    e: ReactPointerEvent<HTMLButtonElement>,
  ) => void;
  resetColumnWidth: (key: string, declarativeWidth?: string) => void;
  resizingKey: string | null;
  actionColumnWidthPx: number;
} {
  const columnKeys = useMemo(() => columns.map((c) => c.key), [columns]);
  const columnKeysKey = columnKeys.join('\0');
  const columnsSpecKey = columns.map((c) => `${c.key}:${c.width ?? ''}`).join('\0');
  const actionColumnWidthPx = compactActions
    ? hasRowLeadingActions && !hasRowActionsMenu
      ? ACTION_COLUMN_COMPACT_LEADING_ONLY_WIDTH_PX
      : ACTION_COLUMN_COMPACT_WIDTH_PX
    : hasRowLeadingActions
      ? ACTION_COLUMN_WITH_LEADING_WIDTH_PX
      : ACTION_COLUMN_WIDTH_PX;

  const [internalSizing, setInternalSizing] = useState<ColumnSizingState>({});
  const columnSizing = controlledSizing ?? internalSizing;
  const userSizedRef = useRef(false);
  const [scrollHostWidthPx, setScrollHostWidthPx] = useState(0);

  const setColumnSizing = useCallback(
    (updater: ColumnSizingState | ((prev: ColumnSizingState) => ColumnSizingState)) => {
      const prev = controlledSizing ?? internalSizing;
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (onSizingChange) onSizingChange(next);
      else setInternalSizing(next);
    },
    [controlledSizing, internalSizing, onSizingChange],
  );

  const columnDefs = useMemo(
    () => buildColumnDefs(columns, hasActionColumn, actionColumnWidthPx),
    [columns, hasActionColumn, actionColumnWidthPx],
  );

  const pinningKey = `${columnPinning.left.join(',')}|${columnPinning.right.join(',')}`;

  const table = useReactTable({
    data,
    columns: columnDefs,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => rowKey(row),
    columnResizeMode: 'onChange',
    enableColumnResizing: columnResizeEnabled,
    enableColumnPinning,
    defaultColumn: {
      minSize: DATA_TABLE_MIN_COLUMN_WIDTH_PX,
      size: DATA_TABLE_DEFAULT_COLUMN_WIDTH_PX,
    },
    state: {
      columnSizing,
      columnPinning,
    },
    onColumnSizingChange: (updater) => {
      userSizedRef.current = true;
      setColumnSizing(updater);
    },
  });

  const resizingActive = table.getState().columnSizingInfo.isResizingColumn;
  const resizingKey = typeof resizingActive === 'string' ? resizingActive : null;

  useLayoutEffect(() => {
    const el = tableRef.current;
    const scrollHost = el?.parentElement;
    if (!scrollHost) return;
    const syncHostWidth = () => {
      const w = Math.max(scrollHost.clientWidth, 320);
      setScrollHostWidthPx((prev) => (prev === w ? prev : w));
    };
    syncHostWidth();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(syncHostWidth) : null;
    ro?.observe(scrollHost);
    return () => ro?.disconnect();
  }, [tableRef, columnKeysKey, hasActionColumn]);

  useLayoutEffect(() => {
    if (!columnResizeEnabled) return;
    const el = tableRef.current;
    if (!el) return;

    const syncSizing = () => {
      const scrollHost = el.parentElement;
      const hostW = Math.max(scrollHost?.clientWidth ?? 0, 320);
      if (userSizedRef.current) return;
      const next = computeColumnSizingFromSpecs(columns, hostW, hasActionColumn, actionColumnWidthPx);
      setColumnSizing((prev) => {
        const same =
          columnKeys.length > 0 && columnKeys.every((key) => prev[key] === next[key]);
        return same ? prev : next;
      });
    };

    syncSizing();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(syncSizing) : null;
    ro?.observe(el);
    const scrollHost = el.parentElement;
    if (scrollHost) ro?.observe(scrollHost);

    return () => {
      userSizedRef.current = false;
      ro?.disconnect();
    };
  }, [
    columnResizeEnabled,
    columnKeysKey,
    columnsSpecKey,
    columns,
    columnKeys,
    hasActionColumn,
    actionColumnWidthPx,
    tableRef,
    setColumnSizing,
  ]);

  useEffect(() => {
    if (controlledSizing) setInternalSizing(controlledSizing);
  }, [controlledSizing]);

  const getWidthPx = useCallback(
    (key: string, declarativeWidth?: string) => {
      const col = table.getColumn(key);
      if (col) return col.getSize();
      const stored = columnSizing[key];
      if (stored != null && stored >= DATA_TABLE_MIN_COLUMN_WIDTH_PX) return stored;
      const el = tableRef.current;
      const tableWidthPx = el?.offsetWidth || 800;
      return parseDataTableColumnWidth(declarativeWidth, tableWidthPx, DATA_TABLE_DEFAULT_COLUMN_WIDTH_PX);
    },
    [table, columnSizing, tableRef],
  );

  const getColumnStyle = useCallback(
    (key: string, declarativeWidth?: string): CSSProperties => {
      if (key === '__actions') {
        return {
          width: actionColumnWidthPx,
          minWidth: actionColumnWidthPx,
          maxWidth: actionColumnWidthPx,
        };
      }
      if (!columnResizeEnabled) {
        if (horizontalScrollLayout) {
          const pinStartKey = columnPinning.left[0];
          if (pinStartKey && key === pinStartKey) return horizontalScrollPinStartStyle();
          return horizontalScrollDataColumnStyle(declarativeWidth);
        }
        return declarativeWidth ? { width: declarativeWidth } : {};
      }
      return columnWidthStyle(getWidthPx(key, declarativeWidth), declarativeWidth);
    },
    [columnResizeEnabled, getWidthPx, actionColumnWidthPx, horizontalScrollLayout, columnPinning.left],
  );

  useLayoutEffect(() => {
    if (!hasActionColumn) return;
    setColumnSizing((prev) => {
      if (prev.__actions === actionColumnWidthPx) return prev;
      return { ...prev, __actions: actionColumnWidthPx };
    });
  }, [hasActionColumn, actionColumnWidthPx]);

  const getPinnedColumnStyle = useCallback(
    (key: string, isHeader?: boolean): CSSProperties => {
      if (!enableColumnPinning) return {};
      const col = table.getColumn(key);
      if (!col) return {};
      return getWpPinnedStyles<T>(col, { isHeader, stickyEndInsetPx });
    },
    [enableColumnPinning, table, stickyEndInsetPx, pinningKey],
  );

  const mergeCellStyles = useCallback(
    (key: string, declarativeWidth: string | undefined, isHeader?: boolean): CSSProperties => ({
      ...getColumnStyle(key, declarativeWidth),
      ...getPinnedColumnStyle(key, isHeader),
    }),
    [getColumnStyle, getPinnedColumnStyle],
  );

  const pinStartColumnKey = columnPinning.left[0];

  const resetColumnWidth = useCallback(
    (_key: string, _declarativeWidth?: string) => {
      const el = tableRef.current;
      const scrollHost = el?.parentElement;
      const hostW = Math.max(scrollHost?.clientWidth ?? 0, el?.offsetWidth ?? 0, 320);
      userSizedRef.current = false;
      setColumnSizing(
        computeColumnSizingFromSpecs(columns, hostW, hasActionColumn, actionColumnWidthPx),
      );
    },
    [columns, hasActionColumn, actionColumnWidthPx, setColumnSizing, tableRef],
  );

  const onResizePointerDown = useCallback(
    (key: string, _declarativeWidth: string | undefined, e: ReactPointerEvent<HTMLButtonElement>) => {
      if (!columnResizeEnabled || e.button !== 0) return;
      const header = table.getFlatHeaders().find((h) => h.column.id === key);
      const handler = header?.getResizeHandler();
      if (!handler) return;
      e.preventDefault();
      e.stopPropagation();
      handler(e);
    },
    [columnResizeEnabled, table],
  );

  const tableWidthPx = columnResizeEnabled
    ? sumColumnWidths(columnSizing, columnKeys, hasActionColumn, actionColumnWidthPx)
    : undefined;

  const colGroupCols = useMemo(() => {
    const leaves = table.getVisibleLeafColumns();
    if (leaves.length === 0) return null;

    if (columnResizeEnabled && tableWidthPx != null && tableWidthPx > 0) {
      return leaves.map((leafCol) => {
        const key = leafCol.id;
        const wp = columns.find((c) => c.key === key);
        const widthPx =
          key === '__actions' ? actionColumnWidthPx : getWidthPx(key, wp?.width);
        return {
          key,
          widthPx,
          widthPct: colgroupPercentWidth(widthPx, tableWidthPx),
        };
      });
    }

    if (horizontalScrollLayout) {
      const sizing = clampPinStartSizingPx(
        computeColumnSizingFromSpecs(
          columns,
          DATA_TABLE_HORIZONTAL_SCROLL_REF_PX,
          hasActionColumn,
          actionColumnWidthPx,
        ),
        pinStartColumnKey,
      );

      return leaves.map((leafCol) => {
        const key = leafCol.id;
        const widthPx =
          key === '__actions'
            ? actionColumnWidthPx
            : (sizing[key] ?? getWidthPx(key, columns.find((c) => c.key === key)?.width));
        return {
          key,
          widthPx,
          widthPct: `${widthPx}px`,
        };
      });
    }

    if (!hasActionColumn) return null;

    const el = tableRef.current;
    const hostW = Math.max(
      scrollHostWidthPx,
      el?.offsetWidth ?? 0,
      el?.parentElement?.clientWidth ?? 0,
      320,
    );
    const sizing = computeColumnSizingFromSpecs(columns, hostW, hasActionColumn, actionColumnWidthPx);

    return leaves.map((leafCol) => {
      const key = leafCol.id;
      if (key === '__actions') {
        return {
          key,
          widthPx: actionColumnWidthPx,
          widthPct: `${actionColumnWidthPx}px`,
        };
      }
      const wp = columns.find((c) => c.key === key);
      const widthPx = sizing[key] ?? getWidthPx(key, wp?.width);
      return {
        key,
        widthPx,
        widthPct: colgroupPercentWidth(widthPx, hostW),
      };
    });
  }, [
    table,
    columnResizeEnabled,
    tableWidthPx,
    columnKeysKey,
    columns,
    hasActionColumn,
    actionColumnWidthPx,
    getWidthPx,
    horizontalScrollLayout,
    pinStartColumnKey,
    scrollHostWidthPx,
  ]);

  return {
    table,
    colGroupCols,
    tableWidthPx,
    getColumnStyle,
    mergeCellStyles,
    onResizePointerDown,
    resetColumnWidth,
    resizingKey,
    actionColumnWidthPx,
  };
}

export type DataTableCoreRow<T> = Row<T>;

/** @deprecated Use `useDataTableCore` */
export const useWpDataTable = useDataTableCore;
